import { CommentsFactory } from '@getaccept/lib-shared/src/contextual-commenting/helpers/comments.factory';
import { CommentsHelper } from '@getaccept/lib-shared/src/contextual-commenting/helpers/comments.helper';
import type { Comment } from '@getaccept/lib-shared/src/contextual-commenting/types/comment';
import type { CommentParticipant } from '@getaccept/lib-shared/src/contextual-commenting/types/comment-participant';
import type { CommentEditorSelection } from '@getaccept/lib-shared/src/contextual-commenting/types/comment-selection';
import { CommentStatus } from '@getaccept/lib-shared/src/contextual-commenting/types/comment-status';
import type { CreateCommentDto } from '@getaccept/lib-shared/src/contextual-commenting/types/create-comment-dto';
import type { CreateReplyInput } from '@getaccept/lib-shared/src/contextual-commenting/types/create-reply-input';
import type { SetContextualCommentAndRepliesReadbyInput } from '@getaccept/lib-shared/src/contextual-commenting/types/set-contextual-comment-and-replies-readby-input';
import type { UpdateCommentDto } from '@getaccept/lib-shared/src/contextual-commenting/types/update-comment-dto';
import type { SigningPageDocument } from '@getaccept/lib-shared/src/types/signing-page-document';
import type { Ref } from '@vue/composition-api';
import { ref, unref } from '@vue/composition-api';
import type { MaybeRef } from '@vueuse/core';
import { nanoid } from 'nanoid';
import type {
  ContextualCommentBase,
  CommentUpdatedPayload,
  ReplyCreatedPayload,
  ReplyReadPayload,
} from '@getaccept/lib-shared/src/pusher/contextual-commenting/document-channel-payloads';
import type {
  CommentCreatedPayload,
  EditorSelectionCreatedPayload,
  CommentUpdatedPayload as RecipientcommentUpdatedPayload,
} from '../../pusher/contextual-commenting/recipient-channel-payloads';
import { CommentsService } from '../../api/contextual-commenting/services/comments.service';

const comments: Ref<Comment[]> = ref([]);
const selectedCommentId = ref('');

const loading = ref(false);
const loaded = ref(false);
const loadFailed = ref(false);

export const useContextualComments = () => {
  const loadComments = async (
    statuses?: MaybeRef<CommentStatus[]>,
    limit?: MaybeRef<number>,
    offset?: MaybeRef<number>
  ) => {
    try {
      loading.value = true;
      loadFailed.value = false;
      comments.value = await CommentsService.load(unref(statuses), unref(limit), unref(offset));
      loaded.value = true;
    } catch (e) {
      loadFailed.value = true;
    } finally {
      loading.value = false;
    }
  };

  const createComment = async (
    editorSelection: MaybeRef<CommentEditorSelection>,
    pageId: MaybeRef<string>,
    createdBy: MaybeRef<CommentParticipant>
  ) => {
    const payload: CreateCommentDto = {
      text: '',
      pageId: unref(pageId),
      status: CommentStatus.Draft,
      editorSelection: unref(editorSelection),
    };

    const newComment = CommentsFactory.comment({
      id: `${CommentsHelper.TEMP_COMMENT_ID}${nanoid()}`,
      createdBy: unref(createdBy),
      pageId: unref(pageId),
      editorSelection: unref(editorSelection),
    });

    comments.value = [...comments.value, newComment];

    try {
      const createdComment = await CommentsService.create(payload);
      comments.value = [...comments.value.filter(({ id }) => id !== newComment.id), createdComment];

      return createdComment;
    } catch (e) {
      comments.value = comments.value.filter(({ id }) => id !== newComment.id);
      return null;
    }
  };

  const updateComment = async (payload: MaybeRef<Partial<UpdateCommentDto>>) => {
    const commentPartial = unref(payload);
    const staleComment = comments.value.find(({ id }) => id === commentPartial.commentId);

    comments.value = CommentsHelper.updateComment(
      commentPartial,
      commentPartial.commentId,
      comments.value
    );

    try {
      await CommentsService.update(commentPartial);
    } catch (e) {
      comments.value = CommentsHelper.updateComment(staleComment, staleComment.id, comments.value);
    }
  };

  const createReply = async (
    payload: MaybeRef<CreateReplyInput>,
    recipientId: MaybeRef<string>
  ) => {
    const createReplyInput = unref(payload);

    const participant = CommentsFactory.commentParticipant({
      recipientId: unref(recipientId),
    });

    const newReply = CommentsFactory.reply({
      createdBy: participant,
      reads: [{ participant, timestamp: Date.now() }],
      ...createReplyInput,
    });

    comments.value = CommentsHelper.addReply(newReply, createReplyInput.commentId, comments.value);

    try {
      const createdReply = await CommentsService.createReply(createReplyInput);
      comments.value = CommentsHelper.updateReply(
        createdReply,
        newReply.id,
        newReply.commentId,
        comments.value
      );
    } catch (e) {
      comments.value = CommentsHelper.removeReply(newReply.id, newReply.commentId, comments.value);
    }
  };

  const readCommentsAndReplies = async (
    payload: MaybeRef<SetContextualCommentAndRepliesReadbyInput>,
    recipientId: MaybeRef<string>
  ) => {
    const { commentIds, replies } = unref(payload);
    const readEvent = CommentsFactory.readEvent({
      participant: CommentsFactory.commentParticipant({
        recipientId: unref(recipientId),
      }),
    });

    const oldComments = [...comments.value];

    commentIds.forEach(commentId => {
      comments.value = CommentsHelper.addRead(readEvent, commentId, comments.value);
    });

    replies.forEach(({ commentId, replyIds }) => {
      replyIds.forEach(replyId => {
        comments.value = CommentsHelper.addReplyRead(readEvent, replyId, commentId, comments.value);
      });
    });

    try {
      await CommentsService.setReadBy({ commentIds, replies });
    } catch (e) {
      comments.value = [...oldComments];
    }
  };

  const selectComment = (commentId: MaybeRef<string>, recipientId: MaybeRef<string>) => {
    selectedCommentId.value = unref(commentId);
    const _recipientId = unref(recipientId);

    const selectedComment = comments.value.find(comment => comment.id === commentId);

    if (!selectedComment) {
      return;
    }

    const isCommentUnread = !CommentsHelper.isReadByRecipient(selectedComment.reads, _recipientId);
    const unreadReplies = CommentsHelper.getUnreadRepliesByRecipient(
      selectedComment.replies,
      _recipientId
    );

    if (!isCommentUnread && !unreadReplies.length) {
      return;
    }

    const commentAndRepliesReadbyInput: SetContextualCommentAndRepliesReadbyInput = {
      commentIds: isCommentUnread ? [selectedComment.id] : [],
      replies: unreadReplies.length
        ? [{ commentId: selectedComment.id, replyIds: unreadReplies.map(({ id }) => id) }]
        : [],
    };

    readCommentsAndReplies(commentAndRepliesReadbyInput, _recipientId);
  };

  const updateDetachedFromVersion = (document: SigningPageDocument) => {
    const idVersionList = (document.pages || [])
      .filter(page => page.editorBlock)
      .reduce((acc, page) => {
        const { id, contentVersion } = page.editorBlock;
        acc[id] = contentVersion;
        return acc;
      }, {});

    comments.value = comments.value.map(comment => {
      if (!comment.editorSelection) {
        return comment;
      }

      const detachedFromVersion =
        idVersionList[comment.editorSelection.editorBlockId] !==
        comment.editorSelection.editorBlockVersion;

      return { ...comment, editorSelection: { ...comment.editorSelection, detachedFromVersion } };
    });
  };

  const onCommentUpdated = async (documentId: string, payload: CommentUpdatedPayload) => {
    if (
      !documentId ||
      !payload?.contextualComment?.commentId ||
      !payload?.contextualComment?.comment
    ) {
      return;
    }

    const {
      contextualComment: { commentId, comment: data },
    } = payload;

    if (CommentsHelper.commentAlreadyUpdated(comments.value, commentId, data)) {
      return;
    }

    try {
      const updatedComment: Comment = await CommentsService.loadOne(documentId, commentId);
      if (comments.value.some((comment: Comment) => comment.id === updatedComment.id)) {
        comments.value = comments.value.map((comment: Comment) =>
          comment.id === updatedComment.id ? updatedComment : comment
        );
        return;
      }
      comments.value = [...comments.value, updatedComment];
    } catch (e) {
      console.error('Could not load comment', commentId, e);
    }
  };

  const onRecipientCommentUpdated = (payload: RecipientcommentUpdatedPayload) => {
    if (!payload?.contextualComment?.commentId || !payload?.contextualComment?.comment) {
      return;
    }

    const {
      contextualComment: { commentId, comment: data },
    } = payload;

    if (CommentsHelper.commentAlreadyUpdated(comments.value, commentId, data)) {
      return;
    }

    if (comments.value.some((comment: Comment) => comment.id === commentId)) {
      comments.value = comments.value.map((comment: Comment) =>
        comment.id === commentId ? { ...comment, ...data } : comment
      );
    }
  };

  const onReplyCreated = (payload: ReplyCreatedPayload) => {
    if (!payload?.contextualComment?.commentId || !payload?.contextualComment?.reply) {
      return;
    }
    const {
      contextualComment: { commentId, reply },
    } = payload;

    if (CommentsHelper.replyExists(comments.value, commentId, reply.id)) {
      return;
    }
    comments.value = CommentsHelper.addReply(reply, commentId, comments.value);
  };

  const onCommentRead = (payload: ContextualCommentBase) => {
    if (!payload?.contextualComment?.createdBy || !payload?.contextualComment?.commentId) {
      return;
    }
    const {
      contextualComment: {
        commentId,
        createdBy: { userId, recipientId },
      },
    } = payload;
    const comment = CommentsHelper.findComment(comments.value, commentId);
    if (!comment) {
      return;
    }

    const isRead = CommentsHelper.isReadByUser(comment.reads, userId || recipientId);
    if (isRead) {
      return;
    }

    const readEvent = CommentsFactory.readEvent({
      participant: CommentsFactory.commentParticipant({ userId, recipientId }),
    });
    comments.value = CommentsHelper.addRead(readEvent, commentId, comments.value);
  };

  const onReplyRead = (payload: ReplyReadPayload) => {
    if (
      !payload?.contextualComment?.commentId ||
      !payload?.contextualComment?.replyId ||
      !payload?.contextualComment?.createdBy
    ) {
      return;
    }
    const {
      contextualComment: {
        commentId,
        replyId,
        createdBy: { userId, recipientId },
      },
    } = payload;

    const reply = CommentsHelper.findReply(comments.value, commentId, replyId);
    if (!reply) {
      return;
    }

    const isRead = CommentsHelper.isReadByUser(reply.reads, userId || recipientId);
    if (isRead) {
      return;
    }

    const readEvent = CommentsFactory.readEvent({
      participant: CommentsFactory.commentParticipant({ userId, recipientId }),
    });

    comments.value = CommentsHelper.addReplyRead(readEvent, replyId, commentId, comments.value);
  };

  const onCommentCreated = (payload: CommentCreatedPayload) => {
    if (!payload?.contextualComment?.comment) {
      return;
    }

    const {
      contextualComment: { comment },
    } = payload;

    if (CommentsHelper.commentExists(comments.value, comment.id)) {
      return;
    }

    comments.value = CommentsHelper.addComment(comment, comments.value);
  };

  const onEditorSelectionCreated = (payload: EditorSelectionCreatedPayload) => {
    if (!payload?.contextualComment?.commentId || !payload?.contextualComment?.editorSelection) {
      return;
    }

    const {
      recipientId,
      contextualComment: { editorSelection, commentId },
    } = payload;

    const comment = CommentsHelper.findComment(comments.value, commentId);

    if (!comment) {
      const createdBy: CommentParticipant = { recipientId, userId: null };
      const comment = CommentsFactory.comment({
        editorSelection,
        id: commentId,
        createdBy,
      });
      comments.value = CommentsHelper.addComment(comment, comments.value);
      return;
    }
    const editorSelectionAlreadyUpdated = comment.editorSelection === editorSelection;
    if (editorSelectionAlreadyUpdated) {
      return;
    }

    comments.value = CommentsHelper.updateComment({ editorSelection }, commentId, comments.value);
  };

  return {
    loading,
    loaded,
    loadFailed,
    comments,
    selectedCommentId,
    loadComments,
    createComment,
    updateComment,
    readCommentsAndReplies,
    createReply,
    selectComment,
    updateDetachedFromVersion,
    onCommentUpdated,
    onReplyCreated,
    onCommentRead,
    onReplyRead,
    onCommentCreated,
    onEditorSelectionCreated,
    onRecipientCommentUpdated,
  };
};
